/**
 * Payment History Component
 * Displays user's credit transaction history
 */

import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Clock, TrendingUp, TrendingDown, Gift, ShoppingCart } from 'lucide-react';
import { apiClient } from '@/utils/apiClient';
import { format } from 'date-fns';

interface PaymentHistoryItem {
  id: string;
  transaction_type: string;
  credits_amount: number;
  package_id: string | null;
  stripe_payment_intent_id: string | null;
  notes: string | null;
  created_at: string;
}

interface PaymentHistoryData {
  transactions: PaymentHistoryItem[];
  total_purchased: number;
  total_used: number;
  current_balance: number;
}

export const PaymentHistory = () => {
  const { data, isLoading } = useQuery<PaymentHistoryData>({
    queryKey: ['payment-history'],
    queryFn: async () => {
      return await apiClient.get<PaymentHistoryData>(
        '/payments/history',
        { includeAuth: true }
      );
    },
    staleTime: 60 * 1000, // 1 minute
  });

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'purchase':
        return <ShoppingCart className="h-4 w-4 text-green-600" />;
      case 'bonus':
        return <Gift className="h-4 w-4 text-purple-600" />;
      case 'usage':
        return <TrendingDown className="h-4 w-4 text-orange-600" />;
      case 'refund':
        return <TrendingUp className="h-4 w-4 text-blue-600" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getTransactionBadge = (type: string) => {
    const variants = {
      purchase: 'default',
      bonus: 'secondary',
      usage: 'outline',
      refund: 'destructive',
    };

    return (
      <Badge variant={variants[type as keyof typeof variants] || 'outline'} className="capitalize">
        {type}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
          <CardDescription>Loading transaction history...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-muted rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" />
          Payment History
        </CardTitle>
        <CardDescription>
          Your credit transactions and purchases
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-muted/50 rounded-lg">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{data.total_purchased}</p>
            <p className="text-xs text-muted-foreground">Total Purchased</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-orange-600">{data.total_used}</p>
            <p className="text-xs text-muted-foreground">Total Used</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-primary">{data.current_balance}</p>
            <p className="text-xs text-muted-foreground">Current Balance</p>
          </div>
        </div>

        {/* Transaction Table */}
        {data?.transactions && data.transactions.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getTransactionIcon(transaction.transaction_type)}
                        {getTransactionBadge(transaction.transaction_type)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={
                        transaction.transaction_type === 'usage' ? 'text-orange-600' : 'text-green-600'
                      }>
                        {transaction.transaction_type === 'usage' ? '-' : '+'}
                        {Math.abs(transaction.credits_amount)}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {transaction.notes || 'No description'}
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {format(new Date(transaction.created_at), 'MMM d, yyyy')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No transaction history yet</p>
            <p className="text-sm">Purchase credits to see your history here</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
